import { Fragment, useContext, useEffect, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { FundsTransferWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import { BaseAlertModal } from "../../../components/modal/alert";
import { AlertModalCheckIcon } from "../../../assets";
import {
	Box,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { BaseButton } from "../../../components/button/styled";
import Cookies from "universal-cookie";
import { fundsTransferService } from "../../../utils/api/transaction/fundstransfer/postTransfer";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseSelect } from "../../../components/form/select/styled";
import { BaseOption } from "../../../components/form/option/styled";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllInstitutionService } from "../../../utils/outbound/institution/retrieveAllInstitution";
import { retrieveUserProfileService } from "../../../utils/api/usermanagement/profile/retrieveUser";
import { useDebounce } from "use-debounce";
import { retrieveSavingsAccountByAccountNumberService } from "../../../utils/outbound/savingsaccount/retrieveSavingsAccountByAccountNumber";
import { retrieveCustomerService } from "../../../utils/api/customerservice/customerlist/retrieveCustomer";
import { retrieveGroupService } from "../../../utils/api/customerservice/grouplist/retrieveGroup";
import { retrieveOtherMFISavingsAccountService } from "../../../utils/outbound/savingsaccount/retrieveOtherMFISavingsAccount";
import { retrieveOtherMFICustomerService } from "../../../utils/api/customerservice/customerlist/retrieveOtherMFICustomer";
import { retrieveOtherMFIGroupService } from "../../../utils/api/customerservice/grouplist/retrieveOtherMFIGroup";
import { BaseConfirmationModal } from "../../../components/modal/confirmation";

export const FundsTransfer = () => {
	const tabIndex = { start: 0, end: 2 };
	const transferTypes = ["MFI Customer", "Other MFI Customer", "Outband"];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const { setActiveNavItem } = useContext(AppContext);

	const [activeTab, setActiveTab] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isMFICustomer, setIsMFICustomer] = useState(true);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
	const [formDetails, setFormDetails] = useState({
		type: " ",
		amount: "",
		narration: "",
		senderAccountName: "",
		senderAccountNumber: "",
		senderAccountBalance: "",
		recipientAccountName: "",
		recipientAccountNumber: "",
		...(!isMFICustomer && { institution: " " }),
	});

	const [debouncedSenderAccountRef] = useDebounce(
		formDetails.senderAccountNumber,
		500
	);
	const [debouncedRecipientAccountRef] = useDebounce(
		formDetails.recipientAccountNumber,
		500
	);

	useEffect(() => {
		setActiveNavItem("Transactions");
	}, [setActiveNavItem]);

	useEffect(() => {
		if (!debouncedRecipientAccountRef.trim()) return;
		if (
			!transferTypes
				.filter((type) => type !== "Outband")
				.includes(formDetails.type)
		)
			return;
		let isActive = true;
		const controller = new AbortController();
		const fetchRecipientBankAccountInfo = async () => {
			try {
				setFormDetails((prev) => ({
					...prev,
					recipientAccountName: "",
				}));
				const savingsAccount =
					formDetails.type === transferTypes[0]
						? await retrieveSavingsAccountByAccountNumberService(
								TOKEN,
								debouncedRecipientAccountRef,
								controller.signal
						  )
						: await retrieveOtherMFISavingsAccountService(
								TOKEN,
								debouncedRecipientAccountRef,
								formDetails?.institution ? formDetails?.institution : "",
								controller.signal
						  );
				if (!isActive) return;
				if (!savingsAccount) {
					setFormDetails((prev) => ({
						...prev,
						recipientAccountName: "",
					}));
					return;
				}
				let recipient: Record<string, any>;
				if (savingsAccount?.ownerType === "individual") {
					recipient =
						formDetails.type === transferTypes[0]
							? await retrieveCustomerService(
									TOKEN,
									savingsAccount.ownerId,
									controller.signal
							  )
							: await retrieveOtherMFICustomerService(
									TOKEN,
									formDetails?.institution ? formDetails?.institution : "",
									savingsAccount.ownerId,
									controller.signal
							  );
				} else {
					recipient =
						formDetails.type === transferTypes[0]
							? await retrieveGroupService(
									TOKEN,
									savingsAccount.ownerId,
									controller.signal
							  )
							: await retrieveOtherMFIGroupService(
									TOKEN,
									formDetails?.institution ? formDetails?.institution : "",
									savingsAccount.ownerId,
									controller.signal
							  );
				}
				if (!isActive) return;
				setFormDetails((prev) => ({
					...prev,
					recipientAccountName:
						savingsAccount?.ownerType === "individual"
							? `${recipient?.firstName ?? ""} ${recipient?.lastName ?? ""}`
							: recipient?.name,
				}));
			} catch (err: any) {
				if (err.name !== "AbortError") {
					console.error("Failed to fetch customer:", err);
					setFormDetails((prev) => ({
						...prev,
						recipientAccountName: "",
					}));
				}
			}
		};
		fetchRecipientBankAccountInfo();
		return () => {
			isActive = false;
			controller.abort();
		};
		// eslint-disable-next-line
	}, [
		TOKEN,
		debouncedRecipientAccountRef,
		formDetails.type,
		formDetails.institution,
	]);

	useEffect(() => {
		if (!debouncedSenderAccountRef.trim()) return;
		let isActive = true;
		const controller = new AbortController();
		const fetchSenderBankAccountInfo = async () => {
			try {
				setFormDetails((prev) => ({
					...prev,
					senderAccountName: "",
					senderAccountBalance: "",
				}));
				const savingsAccount =
					await retrieveSavingsAccountByAccountNumberService(
						TOKEN,
						debouncedSenderAccountRef,
						controller.signal
					);
				if (!isActive) return;
				if (!savingsAccount) {
					setFormDetails((prev) => ({
						...prev,
						senderAccountName: "",
						senderAccountBalance: "",
					}));
					return;
				}
				let sender: Record<string, any>;
				if (savingsAccount?.ownerType === "individual") {
					sender = await retrieveCustomerService(
						TOKEN,
						savingsAccount.ownerId,
						controller.signal
					);
				} else {
					sender = await retrieveGroupService(
						TOKEN,
						savingsAccount.ownerId,
						controller.signal
					);
				}
				if (!isActive) return;
				setFormDetails((prev) => ({
					...prev,
					senderAccountName:
						savingsAccount?.ownerType === "individual"
							? `${sender?.firstName ?? ""} ${sender?.lastName ?? ""}`
							: sender?.name,
					senderAccountBalance: savingsAccount?.balance,
				}));
			} catch (err: any) {
				if (err.name !== "AbortError") {
					console.error("Failed to fetch customer:", err);
					setFormDetails((prev) => ({
						...prev,
						senderAccountName: "",
						senderAccountBalance: "",
					}));
				}
			}
		};
		fetchSenderBankAccountInfo();
		return () => {
			isActive = false;
			controller.abort();
		};
	}, [TOKEN, debouncedSenderAccountRef]);

	const { data: systemMFIs } = useQuery({
		queryKey: ["systemMFIs", TOKEN],
		queryFn: async () => {
			const user = await retrieveUserProfileService(TOKEN);
			const allInstitution = await retrieveAllInstitutionService(user?._id);
			return allInstitution;
		},
		enabled: !!TOKEN,
	});

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		return setIsAlertModalOpen(false);
	};

	const handleConfirmationModalPersist = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>
	) => {
		const { name, value } = e.target as { name: string; value: string };
		if (isMFICustomer && name === "institution") return;
		setFormDetails((prev) => {
			if (name === "type") {
				const { institution, ...rest } = prev;
				const resetFields = {
					recipientAccountName: "",
				};
				if (value === transferTypes[0]) {
					return { ...rest, ...resetFields, [name]: value };
				} else {
					return {
						...rest,
						...resetFields,
						[name]: value,
						institution: " ",
					};
				}
			}
			return { ...prev, [name]: value };
		});
		if (name === "type") {
			const isCustomer =
				!!(value as string).trim() && value === transferTypes[0];
			setIsMFICustomer(isCustomer);
		}
	};

	const handlePagination = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		action: "previous" | "next"
	) => {
		e.preventDefault();
		const form = document.querySelector("form") as HTMLFormElement | null;
		switch (action) {
			case "previous":
				activeTab === tabIndex.start
					? setActiveTab((prev) => prev)
					: setActiveTab((prev) => prev - 1);
				break;
			case "next":
				if (form && !form.reportValidity()) return;
				activeTab === tabIndex.end - 1
					? setIsConfirmationModalOpen(true)
					: setActiveTab((prev) => prev + 1);
				break;
			default:
				break;
		}
	};

	const handleFundsTransfer = async () => {
		try {
			const response = await fundsTransferService(TOKEN, formDetails);
			if (response.status === "success") {
                setIsLoading(false);
                setIsConfirmationModalOpen(false);
				setIsAlertModalOpen(true);
			} else {
				setIsLoading(false);
				setError(
					"Failed to transfer funds. Please check credentials and try again."
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to transfer funds. ${error.message}`);
			console.error("Failed to transfer funds:", error);
		}
	};

	const handleConfirmationModalCallToActionClick = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		actionButton: string
	) => {
		e.preventDefault();
		e.stopPropagation();
		setError(null);
		switch (actionButton) {
			case "callToActionI":
				setIsLoading(true);
				await handleFundsTransfer();
				break;
			case "callToActionII":
				setIsLoading(false);
				setIsConfirmationModalOpen(false);
				break;
			default:
				break;
		}
	};

	const confirmationModalBody = (
		<Box paddingTop={"calc(var(--basic-padding)/1.5)"}>
			<Stack gap={{ mobile: "calc(var(--flex-gap)/2)" }}>
				<Stack gap={{ mobile: "calc(var(--flex-gap)/4)" }}>
					<Box>
						<Typography
							variant="h3"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={18}
							lineHeight={"normal"}
							color="var(--primary-color)"
						>
							From Account
						</Typography>
					</Box>
					<Box>
						<Typography
							variant="subtitle1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={"16px"}
							lineHeight={"normal"}
							color="var(--semi-dark-color)"
						>
							Account Number:{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={500}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
							>
								{formDetails.senderAccountNumber || "-"}
							</Typography>
						</Typography>
					</Box>
					<Box>
						<Typography
							variant="subtitle1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={"16px"}
							lineHeight={"normal"}
							color="var(--semi-dark-color)"
						>
							Account Name:{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={500}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
							>
								{formDetails.senderAccountName || "-"}
							</Typography>
						</Typography>
					</Box>
				</Stack>
				<Stack gap={{ mobile: "calc(var(--flex-gap)/4)" }}>
					<Box>
						<Typography
							variant="h3"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={18}
							lineHeight={"normal"}
							color="var(--primary-color)"
						>
							To Account
						</Typography>
					</Box>
					<Box>
						<Typography
							variant="subtitle1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={"16px"}
							lineHeight={"normal"}
							color="var(--semi-dark-color)"
						>
							Account Number:{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={500}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
							>
								{formDetails.recipientAccountNumber || "-"}
							</Typography>
						</Typography>
					</Box>
					<Box>
						<Typography
							variant="subtitle1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={"16px"}
							lineHeight={"normal"}
							color="var(--semi-dark-color)"
						>
							Account Name:{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={500}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
							>
								{formDetails.recipientAccountName || "-"}
							</Typography>
						</Typography>
					</Box>
				</Stack>
				<Stack gap={{ mobile: "calc(var(--flex-gap)/4)" }}>
					<Box>
						<Typography
							variant="h3"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={18}
							lineHeight={"normal"}
							color="var(--primary-color)"
						>
							Transfer Details
						</Typography>
					</Box>
					<Box>
						<Typography
							variant="subtitle1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={"16px"}
							lineHeight={"normal"}
							color="var(--semi-dark-color)"
						>
							Transfer Amount:{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={500}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
							>
								{formDetails.amount || "-"}
							</Typography>
						</Typography>
					</Box>
					<Box>
						<Typography
							variant="subtitle1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={"16px"}
							lineHeight={"normal"}
							color="var(--semi-dark-color)"
						>
							Narration:{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={500}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
							>
								{formDetails.narration || "-"}
							</Typography>
						</Typography>
					</Box>
					<Box>
						<Typography
							variant="subtitle1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={"16px"}
							lineHeight={"normal"}
							color="var(--semi-dark-color)"
						>
							Transfer Type:{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={500}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
							>
								{formDetails.type || "-"}
							</Typography>
						</Typography>
					</Box>
				</Stack>
			</Stack>
			{error && activeTab === tabIndex.end - 1 && (
				<Typography
					fontFamily={"Inter"}
					fontWeight={"600"}
					fontSize={14}
					lineHeight={"normal"}
					color={"var(--text-salmon-pink-color)"}
					whiteSpace={"normal"}
					marginBlockStart={"calc(var(--basic-margin)/1.5)"}
				>
					{error}
				</Typography>
			)}
		</Box>
	);

	return (
		<AppLayout pageId={"Funds-Transfer"} pageTitle={"Funds Transfer"}>
			<FundsTransferWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalCheckIcon />}
					title={"Funds Transfer Successful"}
					message="Your funds have been successfully transferred."
					callToAction="OK"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				{/* confirmation modal below */}
				<BaseConfirmationModal
					open={isConfirmationModalOpen}
					title="Review & Confirm"
					message="Please review the details below before submitting:"
					callToActionI="Confirm"
					callToActionII="Cancel"
					isLoadingI={isLoading}
					body={confirmationModalBody}
					handleClose={handleConfirmationModalPersist}
					handleCallToAction={handleConfirmationModalCallToActionClick}
				/>
				{/* confirmation modal above */}
				<Box>
					<Typography
						variant="h3"
						fontFamily={"Poppins"}
						fontWeight={500}
						fontSize={18}
						lineHeight={"normal"}
						color="var(--text-salmon-pink-color)"
					>
						Enter required information to transfer funds.
					</Typography>
				</Box>
				<form>
					<Grid2
						container
						component={"div"}
						spacing={"var(--flex-gap)"}
						className="fieldset-grid-wrapper"
					>
						{activeTab === tabIndex.start && (
							<Fragment>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Transfer Type{" "}
											<Typography
												component={"span"}
												fontFamily={"inherit"}
												fontWeight={"inherit"}
												fontSize={"inherit"}
												lineHeight={"inherit"}
												color={"var(--text-salmon-pink-color)"}
											>
												*
											</Typography>
										</BaseLabel>
										<BaseSelect
											name="type"
											fontweight={400}
											value={formDetails?.type}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value=" ">Select Transfer Type</BaseOption>
											{transferTypes?.map((type: string, index: number) => (
												<BaseOption key={index} value={type}>
													{type}
												</BaseOption>
											))}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								{!isMFICustomer && (
									<Grid2 size={{ mobile: 12, tablet: 6 }}>
										<BaseFieldSet>
											<BaseLabel colour="var(--dark-color)">
												Institution{" "}
												<Typography
													component={"span"}
													fontFamily={"inherit"}
													fontWeight={"inherit"}
													fontSize={"inherit"}
													lineHeight={"inherit"}
													color={"var(--text-salmon-pink-color)"}
												>
													*
												</Typography>
											</BaseLabel>
											<BaseSelect
												name="institution"
												fontweight={400}
												colour="var(--semi-gray-color)"
												value={formDetails?.institution}
												onChange={(e) => handleChange(e)}
											>
												<BaseOption value=" ">Select Institution</BaseOption>
												{(formDetails?.type === "Other MFI Customer"
													? systemMFIs
													: []
												)?.map(
													(institution: Record<string, any>, index: number) => (
														<BaseOption key={index} value={institution.id}>
															{institution.name}
														</BaseOption>
													)
												)}
											</BaseSelect>
										</BaseFieldSet>
									</Grid2>
								)}
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Customer Account Number{" "}
											<Typography
												component={"span"}
												fontFamily={"inherit"}
												fontWeight={"inherit"}
												fontSize={"inherit"}
												lineHeight={"inherit"}
												color={"var(--text-salmon-pink-color)"}
											>
												*
											</Typography>
										</BaseLabel>
										<BaseInput
											required
											name="senderAccountNumber"
											onChange={(e) => handleChange(e)}
											placeholder="Enter Account Number"
											value={formDetails.senderAccountNumber}
										/>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Customer Name
										</BaseLabel>
										<BaseInput
											required
											readOnly
											name="senderAccountName"
											onChange={(e) => handleChange(e)}
											placeholder="Customer Account Name"
											value={formDetails.senderAccountName}
										/>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Available Balance
										</BaseLabel>
										<BaseInput
											required
											readOnly
											name="senderAccountBalance"
											onChange={(e) => handleChange(e)}
											placeholder="Customer Account Balance"
											value={formDetails.senderAccountBalance}
										/>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Transfer Amount{" "}
											<Typography
												component={"span"}
												fontFamily={"inherit"}
												fontWeight={"inherit"}
												fontSize={"inherit"}
												lineHeight={"inherit"}
												color={"var(--text-salmon-pink-color)"}
											>
												*
											</Typography>
										</BaseLabel>
										<BaseInput
											required
											name="amount"
											onChange={(e) => handleChange(e)}
											placeholder="Enter Amount"
											value={formDetails.amount}
										/>
									</BaseFieldSet>
								</Grid2>
							</Fragment>
						)}
						{activeTab === tabIndex.start + 1 && (
							<Fragment>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Recipient Account Number{" "}
											<Typography
												component={"span"}
												fontFamily={"inherit"}
												fontWeight={"inherit"}
												fontSize={"inherit"}
												lineHeight={"inherit"}
												color={"var(--text-salmon-pink-color)"}
											>
												*
											</Typography>
										</BaseLabel>
										<BaseInput
											required
											name="recipientAccountNumber"
											onChange={(e) => handleChange(e)}
											placeholder="Enter Account Number"
											value={formDetails.recipientAccountNumber}
										/>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Recipient Account Name
										</BaseLabel>
										<BaseInput
											required
											readOnly
											name="recipientAccountName"
											onChange={(e) => handleChange(e)}
											placeholder="Recipient Account Name"
											value={formDetails.recipientAccountName}
										/>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Narration{" "}
											<Typography
												component={"span"}
												fontFamily={"inherit"}
												fontWeight={"inherit"}
												fontSize={"inherit"}
												lineHeight={"inherit"}
												color={"var(--text-salmon-pink-color)"}
											>
												*
											</Typography>
										</BaseLabel>
										<BaseInput
											required
											multiline
											minRows={4}
											name="narration"
											value={formDetails.narration}
											placeholder="Enter narration"
											onChange={(e) => handleChange(e)}
										/>
									</BaseFieldSet>
								</Grid2>
							</Fragment>
						)}
					</Grid2>
					<Stack
						gap={"calc(var(--flex-gap)/2)"}
						justifyContent={"space-between"}
						flexDirection={{ mobile: "row" }}
					>
						<Box overflow={"hidden"}>
							<BaseButton
								disableElevation
								variant="contained"
								disabled={activeTab === tabIndex.start}
								sx={{
									width: { mobile: "100%", miniTablet: "auto" },
								}}
								onClick={(e) => handlePagination(e, "previous")}
							>
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Previous
								</Typography>
							</BaseButton>
						</Box>
						<Box overflow={"hidden"}>
							<BaseButton
								disableElevation
								variant="contained"
								sx={{
									width: { mobile: "100%", miniTablet: "auto" },
								}}
								onClick={(e) => handlePagination(e, "next")}
							>
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									{activeTab === tabIndex.end - 1 ? "Submit" : "Next"}
								</Typography>
							</BaseButton>
						</Box>
					</Stack>
				</form>
			</FundsTransferWrapper>
		</AppLayout>
	);
};
